const React = require('react');
const AppProviders = require('./src/providers/AppProviders').default;
const { GatsbyAnalyticsClient } = require('common/src/analytics');
const { ScrollDepthTracker } = require('common/src/analytics/scrollDepth');
const { TimeOnPageTracker } = require('common/src/analytics/timeOnPage');
const { ExitIntentTracker } = require('common/src/analytics/exitIntent');

let analytics;
let scrollDepth;
let timeOnPage;
let exitIntent;

exports.wrapRootElement = ({ element }) =>
  React.createElement(AppProviders, null, element);

// Disable gatsby-plugin-offline's immediate SW registration
// We register manually after page load to avoid blocking first-visit performance
// See: https://github.com/gatsbyjs/gatsby/issues/26059
exports.registerServiceWorker = () => false;

// Deferred analytics initialization to avoid blocking LCP
let analyticsInitialized = false;

function initializeAnalytics() {
  if (analyticsInitialized || typeof window === 'undefined') return;
  analyticsInitialized = true;

  analytics = new GatsbyAnalyticsClient({
    source: 'marketing-site',
    debug: process.env.NODE_ENV === 'development',
  });

  // Initialize engagement trackers
  scrollDepth = new ScrollDepthTracker(analytics);
  timeOnPage = new TimeOnPageTracker(analytics);
  exitIntent = new ExitIntentTracker(analytics);

  // Start initial page tracking
  scrollDepth.start();
  timeOnPage.start();
  exitIntent.start();

  // Initialize Core Web Vitals performance tracking
  // Tracks LCP, FID, CLS, TTFB, DCL, Load for bounce analysis
  analytics.initPerformanceTracking();

  // Expose globally for manual tracking in components
  window.__MTP_ANALYTICS__ = analytics;

  if (process.env.NODE_ENV === 'development') {
    console.log('[Analytics] Initialized after LCP:', analytics);
  }
}

exports.onClientEntry = () => {
  if (typeof window !== 'undefined') {
    // Defer analytics initialization until after LCP to reduce TBT
    // This improves mobile performance by ~100ms
    if ('PerformanceObserver' in window) {
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          lcpObserver.disconnect();
          // Use requestIdleCallback if available for best performance
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(initializeAnalytics, { timeout: 2000 });
          } else {
            setTimeout(initializeAnalytics, 0);
          }
        });
        lcpObserver.observe({
          type: 'largest-contentful-paint',
          buffered: true,
        });
        // Fallback: initialize after 4s if LCP hasn't fired
        setTimeout(initializeAnalytics, 4000);
      } catch (e) {
        // Fallback for browsers that don't support LCP observation
        window.addEventListener('load', initializeAnalytics);
      }
    } else {
      // Fallback for older browsers
      window.addEventListener('load', initializeAnalytics);
    }

    // Deferred service worker registration - runs after page fully loads
    // This avoids blocking first-visit performance (TBT, LCP)
    // See: https://developer.chrome.com/docs/workbox/service-worker-deployment
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker
          .register('/sw.js')
          .then((reg) => {
            if (process.env.NODE_ENV === 'development') {
              console.log('[SW] Registered after page load:', reg.scope);
            }
          })
          .catch((err) => {
            console.error('[SW] Registration failed:', err);
          });
      });
    }
  }
};

// Notify when route changes to update country context
exports.onRouteUpdate = ({ location, prevLocation }) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('gatsby-route-change'));

    // Track page view on every route change
    if (analytics && location.pathname !== prevLocation?.pathname) {
      // Reset trackers for new page
      if (scrollDepth) scrollDepth.reset();
      if (timeOnPage) timeOnPage.reset();
      if (exitIntent) exitIntent.reset();

      // Track page view
      analytics.trackPageView(location.pathname, {
        title: document.title,
        referrer: document.referrer,
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] Page view:', location.pathname);
        console.log('[Analytics] Trackers reset for new page');
      }
    }
  }
};
